import { makeArticle, saintRelated } from "./learnFactory";

export const pageSaintTravelCompanion = makeArticle({
  slug: "can-someone-travel-with-me-for-saint-tms",
  title: "Can Someone Travel With Me for SAINT® TMS? | Neuro Wellness",
  description:
    "Yes, a family member or companion can travel with you for SAINT® TMS. Learn what companions can expect, where to stay, and how to plan for the 5-day treatment week.",
  canonical: "/learn/can-someone-travel-with-me-for-saint-tms",
  h1: "Can Someone Travel With Me for SAINT® TMS?",
  breadcrumbLabel: "Traveling With a Companion for SAINT®",
  shortAnswer:
    "Yes. A family member, friend, or caregiver can travel with you for SAINT® TMS treatment. Because SAINT® is delivered in an outpatient setting with no anesthesia, a companion is optional rather than required — patients can generally drive themselves. However, many patients appreciate having a companion for support during the 5-day treatment week, especially if traveling from out of town. Companions can wait in the clinic or explore the Coral Springs area during sessions and breaks.",
  keyTakeaways: [
    "A companion can travel with you for SAINT® treatment.",
    "A companion is optional — patients can generally drive themselves.",
    "Companions can provide support during the 5-day treatment week.",
    "Neuro Wellness is in Coral Springs, FL, near Fort Lauderdale and Miami airports.",
    "Local hotels and accommodations are available for the treatment week.",
  ],
  sections: [
    {
      heading: "Is a Companion Required?",
      paragraphs: [
        "No. Because SAINT® does not involve anesthesia or sedation and does not impair alertness, patients can generally drive themselves to and from the clinic each day. A companion is optional, not required.",
        "This is different from ECT, which requires an escort home after each treatment due to anesthesia. SAINT® patients do not need an escort.",
      ],
    },
    {
      heading: "Why Some Patients Bring a Companion",
      paragraphs: [
        "Even though a companion is not required, many patients choose to have one for the treatment week, especially if traveling from out of town. A companion can:",
      ],
      list: [
        "Provide emotional support during a significant treatment week.",
        "Help with logistics, meals, and transportation.",
        "Keep the patient company during the 50-minute breaks between sessions.",
        "Attend follow-up appointments and help remember information.",
        "Be present if any side effects or concerns arise.",
      ],
    },
    {
      heading: "What Companions Can Expect",
      paragraphs: [
        "Companions are welcome at Neuro Wellness. During sessions (about 10 minutes each), companions can wait in the clinic or in the private treatment suite. During the 50-minute breaks, companions can spend time with the patient, explore the Coral Springs area, or run errands.",
        "The treatment day runs most of the day for 5 consecutive days. Companions should plan for a full week at the clinic, with time to rest or explore between sessions.",
      ],
    },
    {
      heading: "Travel and Accommodation",
      paragraphs: [
        "Neuro Wellness is located at 9734 W Sample Rd, Coral Springs, FL 33065. The clinic is accessible from both Fort Lauderdale-Hollywood International Airport (FLL, about 30 minutes) and Miami International Airport (MIA, about 45 minutes).",
        "Local hotels are available in Coral Springs and nearby communities. Many patients and their companions stay locally for the 5-day treatment week. Contact Neuro Wellness for recommendations on nearby accommodations.",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "Whether or not to bring a companion is a personal choice. Some patients prefer to come alone and use the breaks for rest or work; others value the support of a family member or friend. Both are perfectly fine.",
        "If you are traveling from out of town, planning ahead for accommodations and transportation will make the treatment week smoother. The Neuro Wellness team can help with recommendations.",
      ],
      links: [
        {
          to: "/traveling-for-treatment",
          text: "See our traveling for treatment guide",
        },
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Can someone travel with me for SAINT® TMS?",
      answer:
        "Yes. A family member, friend, or caregiver can travel with you for SAINT® treatment. A companion is optional — patients can generally drive themselves — but many patients appreciate having support during the 5-day treatment week, especially if traveling from out of town.",
    },
    {
      question: "Do I need someone to drive me to SAINT® treatment?",
      answer:
        "No. Because SAINT® does not involve anesthesia or sedation, patients can generally drive themselves to and from the clinic each day. A companion is optional, not required. If you experience a side effect that affects your ability to drive, tell the treatment team.",
    },
    {
      question: "Can my spouse or family member stay with me during treatment?",
      answer:
        "Yes. Companions are welcome at Neuro Wellness. They can wait in the clinic or private treatment suite during sessions and spend time with you during the 50-minute breaks. The treatment day runs most of the day for 5 consecutive days.",
    },
    {
      question: "Where should I stay during SAINT® treatment in South Florida?",
      answer:
        "Local hotels are available in Coral Springs and nearby communities. Neuro Wellness is about 30 minutes from Fort Lauderdale airport and 45 minutes from Miami airport. Contact the clinic for recommendations on nearby accommodations for the 5-day treatment week.",
    },
    {
      question: "Can I fly after SAINT® treatment?",
      answer:
        "Yes. SAINT® does not involve anesthesia or sedation and does not impose flying restrictions. Patients who travel to Coral Springs for treatment can fly home after completing the 5-day course. Discuss any travel plans with your treatment team.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  relatedLinks: saintRelated,
});
